import { useParams, useSearchParams, Link, Navigate } from "react-router-dom";
import { useFetch } from "../lib/hooks";
import { ArticleCard } from "../components/site/ArticleCard";
import { EmptyState, PageHeader, AdSlot } from "../components/site/Blocks";
import { Seo } from "../components/site/Seo";
import { BrandLoader } from "../components/site/Logo";
import NotFound from "./NotFound";

const Pager = ({ page, pages, go }) =>
  pages > 1 ? (
    <nav aria-label="Stranice" className="mt-12 flex items-center justify-center gap-4" data-testid="pagination">
      <button disabled={page <= 1} onClick={() => go(page - 1)} className="h-11 rounded-sm border border-rule px-4 text-sm font-semibold disabled:opacity-40" data-testid="pagination-prev">← Novije</button>
      <span className="text-sm text-muted-foreground">{page} / {pages}</span>
      <button disabled={page >= pages} onClick={() => go(page + 1)} className="h-11 rounded-sm border border-rule px-4 text-sm font-semibold disabled:opacity-40" data-testid="pagination-next">Starije →</button>
    </nav>
  ) : null;

const Layout = ({ items, first }) => {
  if (!first) return <div className="grid gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-4">{items.map((a) => <ArticleCard key={a.id} a={a} />)}</div>;
  const [lead, ...rest] = items;
  return (
    <>
      <div className="grid gap-6 lg:grid-cols-12 lg:gap-8">
        <div className="lg:col-span-7"><ArticleCard a={lead} variant="feature" testId="category-lead" /></div>
        <div className="divide-y divide-rule lg:col-span-5 lg:-mt-4">{rest.slice(0, 4).map((a) => <ArticleCard key={a.id} a={a} variant="horizontal" />)}</div>
      </div>
      {rest.length > 4 && <div className="mt-10 grid gap-x-6 gap-y-10 border-t border-rule pt-8 sm:grid-cols-2 lg:grid-cols-4">{rest.slice(4).map((a) => <ArticleCard key={a.id} a={a} />)}</div>}
    </>
  );
};

export default function CategoryPage({ mode }) {
  const { categorySlug } = useParams();
  const [params, setParams] = useSearchParams();
  const page = Number(params.get("page") || 1);
  const region = params.get("regija");
  const qs = new URLSearchParams({ page, limit: 16 });
  if (!mode) qs.set("category", categorySlug);
  if (region) qs.set("region", region);
  const { data, loading, error } = useFetch(`/public/articles?${qs}`);

  if (error?.response?.status === 404) return <NotFound />;
  if (data?.redirect) return <Navigate to={data.redirect} replace />;
  const cat = data?.category;
  const title = mode === "latest" ? "Najnovije" : mode === "all" ? "Vijesti" : cat?.name || "";
  const intro = mode === "latest" ? "Hronološki pregled svih objavljenih tekstova." : mode === "all" ? "Sve vijesti, izvještaji, analize i vodiči BalkanPulsa." : cat?.description;
  const items = data?.items || [];
  const go = (p) => setParams({ ...Object.fromEntries(params), page: p });

  return (
    <div className="container-news" data-testid={`category-page-${mode || categorySlug}`}>
      <Seo title={title} description={intro} />
      <nav aria-label="Breadcrumb" className="pt-5 text-[12px] text-muted-foreground" data-testid="breadcrumb">
        <Link to="/" className="hover:underline">Početna</Link> <span aria-hidden>/</span> <span className="text-foreground">{title}</span>
      </nav>
      <PageHeader kicker={mode ? "BalkanPuls.de" : "Rubrika"} title={title} intro={intro} testId="category-header" />
      {!mode && <AdSlot slot="category_banner" category={categorySlug} />}
      {cat?.children?.length > 0 && <nav aria-label="Podrubrike" className="mt-4 flex flex-wrap gap-2 text-sm" data-testid="category-children">{cat.children.map((c) => <Link key={c.slug} to={`/${c.slug}`} className="rounded-full border border-border px-3 py-1 hover:border-pulse">{c.name}</Link>)}</nav>}
      {region && (
        <p className="mt-4 text-sm" data-testid="region-filter">Pokrajina: <strong lang="de">{region}</strong> · <Link to={mode ? `/${mode === "all" ? "vijesti" : "najnovije"}` : `/${categorySlug}`} className="text-pulse underline">ukloni filter</Link></p>
      )}
      <div className="mt-8">
        {loading ? <BrandLoader /> : items.length ? (
          <>
            <div data-testid="category-article-grid"><Layout items={items} first={page === 1} /></div>
            <Pager page={page} pages={data.pages} go={go} />
          </>
        ) : <EmptyState testId="category-empty" />}
      </div>
    </div>
  );
}
